import type { ProjectState } from '.';

import React from 'react';

import type { ProjectContextValue } from '@core/context';

import { useProjectStore } from '.';

export interface InitializeProjectStoreProps {
  children: React.ReactNode;

  /**
   * Initial project store data that represents the starting state for our
   * project store. This data is typically sourced from our SSR props at the
   * root App level.
   */
  data: ProjectState;

  /**
   * Legacy project representation that is used to initialize the store's computed
   * plan properties. We need the legacy mongo representation of the project
   * because the store uses `@readme/iso` code shared with the server.
   */
  legacyData: ProjectContextValue['project'];
}

/**
 * Initializes the ProjectStore with the provided project data just once on the
 * initial render and never again. Use this to initially hydrate the store from
 * SSR props.
 * @example
 * <InitializeProjectStore data={initialProjectData}>
 *   ...
 *</InitializeProjectStore>
 */
export function InitializeProjectStore({ children, data, legacyData }: InitializeProjectStoreProps) {
  const [isReady, reset, initializePlan] = useProjectStore(s => [s.isReady, s.reset, s.plan.initialize]);

  // To prevent the store from ever rendering on the client with unininitialized
  // mock data, we can't do this inside an effect. Instead, we must initialize
  // the store inline but only once before the first mount and never again.
  if (data && legacyData && !isReady) {
    reset(data);
  }

  // Initialize the project plan state. We use the legacy ProjectContext data because the store's plan slice
  // uses `@readme/iso` code shared with the server and that code uses the mongo representation of the
  // project to compute the plan properties.
  if (legacyData && !isReady) {
    initializePlan(legacyData);
  }

  return <>{children}</>;
}
